import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';

interface ProcessingAnalyticsChartsProps {
  days: number;
}

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#9c27b0', '#00897b'];

// Улучшенные названия источников
const getSourceLabel = (sourceType: string, specificSource?: string) => {
  if (sourceType === 'url') {
    if (specificSource === 'youtube') return 'YouTube';
    if (specificSource === 'instagram') return 'Instagram';
    if (specificSource) return specificSource.charAt(0).toUpperCase() + specificSource.slice(1);
    return 'URL';
  }
  if (sourceType === 'telegram') return 'Telegram';
  return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
};

export default function ProcessingAnalyticsCharts({ days }: ProcessingAnalyticsChartsProps) {
  const [includeBlocked, setIncludeBlocked] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['processingAnalytics', days, includeBlocked],
    queryFn: () => statsApi.getProcessingAnalytics(days, includeBlocked),
    keepPreviousData: true,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки аналитики обработки: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  // Подготовка данных для графика сессий
  const sessionsData = data.sessions_by_date.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit'
    }),
    successRate: item.total > 0 ? ((item.successful / item.total) * 100).toFixed(1) : 0,
  }));

  // Подготовка данных для источников с улучшенными названиями
  const sourceData = data.source_distribution.map(item => ({
    name: getSourceLabel(item.source_type, item.specific_source),
    count: item.count,
    percentage: 0, // будет рассчитан далее
  }));

  // Рассчитываем проценты
  const totalSources = sourceData.reduce((sum, item) => sum + item.count, 0);
  sourceData.forEach(item => {
    item.percentage = totalSources > 0 ? (item.count / totalSources * 100) : 0;
  });

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Аналитика обработки файлов
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={includeBlocked}
              onChange={(e) => setIncludeBlocked(e.target.checked)}
              color="primary"
            />
          }
          label="Считать заблокированных пользователей"
        />
      </Box>

      <Grid container spacing={3}>
        {/* График сессий по дням */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Сессии обработки за {days} дней
              </Typography>
              <Box sx={{ height: 350, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sessionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(value) => `Дата: ${value}`}
                      formatter={(value: number, name: string) => {
                        const labels: { [key: string]: string } = {
                          total: 'Всего сессий',
                          successful: 'Успешных',
                          failed: 'Неудачных',
                        };
                        return [value, labels[name] || name];
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      dot={{ fill: '#8884d8' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="successful" 
                      stroke="#82ca9d" 
                      strokeWidth={2}
                      dot={{ fill: '#82ca9d' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="failed"
                      stroke="#d32f2f"
                      strokeWidth={2}
                      dot={{ fill: '#d32f2f' }}
                      name="Неудачных"
                    />
                    <Line
                      type="monotone"
                      dataKey="interrupted"
                      stroke="#ed6c02"
                      strokeWidth={2}
                      dot={{ fill: '#ed6c02' }}
                      name="Прерванных"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Среднее время обработки */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Среднее время обработки
              </Typography>
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h2" color="primary" fontWeight={600}>
                  {data.average_processing_time_seconds.toFixed(1)}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                  секунд
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {(data.average_processing_time_seconds / 60).toFixed(1)} минут в среднем
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Распределение по источникам */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Источники обработки
              </Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, entry: any) => [
                        `${value} сессий (${entry.payload.percentage.toFixed(1)}%)`,
                        entry.payload.name
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry: any) => `${entry.payload.name}: ${entry.payload.count}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Анализ ошибок */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Анализ ошибок
              </Typography>
              {data.error_distribution.length > 0 ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.error_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="error_stage" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value: number) => [value, 'Ошибок']}
                        labelFormatter={(value) => `Этап: ${value}`}
                      />
                      <Bar dataKey="count" fill="#ff8042">
                        {data.error_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: 300,
                  color: 'text.secondary'
                }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    🎉
                  </Typography>
                  <Typography variant="h6">
                    Отлично!
                  </Typography>
                  <Typography variant="body1" textAlign="center">
                    За последние {days} дней ошибок не было
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Дополнительная информация */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Сводка за {days} дней
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                <Chip 
                  label={`Всего сессий: ${data.sessions_by_date.reduce((sum, day) => sum + day.total, 0)}`}
                  variant="outlined" 
                />
                <Chip 
                  label={`Успешных: ${data.sessions_by_date.reduce((sum, day) => sum + day.successful, 0)}`}
                  color="success"
                  variant="outlined" 
                />
                <Chip 
                  label={`С ошибками: ${data.sessions_by_date.reduce((sum, day) => sum + day.failed, 0)}`}
                  color="error"
                  variant="outlined" 
                />
                <Chip 
                  label={`Успешность: ${
                    data.sessions_by_date.length > 0 
                      ? ((data.sessions_by_date.reduce((sum, day) => sum + day.successful, 0) / 
                         data.sessions_by_date.reduce((sum, day) => sum + day.total, 0)) * 100).toFixed(1)
                      : 0
                  }%`}
                  color={
                    data.sessions_by_date.length > 0 && 
                    (data.sessions_by_date.reduce((sum, day) => sum + day.successful, 0) / 
                     data.sessions_by_date.reduce((sum, day) => sum + day.total, 0)) > 0.8 
                      ? "success" : "warning"
                  }
                  variant="outlined" 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}